import { useShadow, useShadowStyle } from "../../util/wcUtil.ts"
import { acceptTerms, logOut, type UserData } from '../../features/serverApi/asApi.ts';
import { envReady } from '../../data/envRegistry.ts';
import { internalData } from '../../store/store.ts';
import { asApp } from '../../util/singletons.ts';
import { closePanel, onPanelButtons, openPanel, panelOverlay } from './modalPanel.ts';
import styleText from './shared-modal-styles.css?raw';

/**
 * Blocks the app until the user accepts the current terms and conditions.
 * Self-contained: it watches `as-auth-update` for a user whose acceptance is
 * out of date, opens itself over the app, and runs both button actions. Same
 * link as the registration form in as-home.
 *
 * Continue stays disabled until the checkbox is ticked, then records the
 * acceptance and closes. Cancel signs the user out and goes to the storefront.
 * The `busy` attribute disables both buttons while a request is in flight.
 *
 * The two button names are private to this component. Nothing outside it
 * listens for them.
 */
export const TERMS_URL = 'http://trinckle.com/appsuite/terms-and-conditions';

const ownStyles = /*css*/`
  .terms-link {
    font-size: 0.85rem;
    color: var(--color-tr-blue, #0084a4);
    text-decoration: underline;
    width: fit-content;
    margin-top: -0.5rem;
  }

  .checkbox-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    cursor: pointer;
    user-select: none;

    & input {
      accent-color: var(--color-tr-blue, #0084a4);
      cursor: pointer;
      margin: 0;
    }

    & as-text {
      width: auto;
    }
  }
`;

export class AsModalAcceptTerms extends HTMLElement {
  static readonly tag = 'as-modal-accept-terms' as const;

  static get observedAttributes() {
    return ['busy'];
  }

  get html() {
    return /*html*/`
      <div class="panel-content">
        <as-text dict-key="apps.general.accept-terms.title" class="modal-title">Terms and Conditions</as-text>
        <as-text dict-key="apps.general.accept-terms.body" class="modal-desc">Please accept the new terms and conditions to continue.</as-text>
        <a class="terms-link" href="${TERMS_URL}" target="_blank" rel="noopener noreferrer"><as-text dict-key="apps.general.accept-terms.link">General Terms and Conditions</as-text></a>
        <label class="checkbox-row">
          <input type="checkbox" id="accept-terms-checkbox">
          <as-text dict-key="apps.general.accept-terms.checkbox">I have read and accept the terms above.</as-text>
        </label>
        <div class="row option-buttons">
          <as-text-button name="decline-terms" dict-key="apps.general.accept-terms.cancel">Cancel</as-text-button>
          <as-text-button name="accept-terms" variant="primary" dict-key="apps.general.accept-terms.continue" disabled>Continue</as-text-button>
        </div>
      </div>
    `;
  }

  get checkbox(): HTMLInputElement | null {
    return this.shadowRoot?.querySelector('#accept-terms-checkbox') ?? null;
  }

  get accepted(): boolean {
    return this.checkbox?.checked ?? false;
  }

  /** The id as-app gives this panel in its overlay markup. The fallback covers
   *  a host that drops the element in without one. */
  private get _panelId(): string {
    return this.id || 'accept-terms';
  }

  connectedCallback() {
    useShadow(this, this.html)
    useShadowStyle(this, styleText + ownStyles, './shared-modal-styles.css')
    this.checkbox?.addEventListener('change', this._syncButtons);
    onPanelButtons(this, {
      'accept-terms':  () => this._accept(),
      'decline-terms': () => this._decline(),
    });
    window.addEventListener('as-auth-update', this._onAuthUpdate);
    this._syncButtons();
  }

  disconnectedCallback() {
    window.removeEventListener('as-auth-update', this._onAuthUpdate);
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'busy' && oldValue !== newValue) this._syncButtons();
  }

  /** Untick the box, e.g. when the modal is opened again after a failure. */
  reset(): void {
    if (this.checkbox) this.checkbox.checked = false;
    this._syncButtons();
  }

  /**
   * Every getUserData() response fires this, not just the bootstrap one, and
   * any of them can carry a null termsAccepted. The storefront is the case that
   * matters: at page load nobody is signed in, so the bootstrap check has
   * nothing to look at, and the sign-in that follows only announces itself
   * through this event.
   */
  private _onAuthUpdate = (e: Event): void => {
    const { isAuthenticated, userData } = (e as CustomEvent).detail ?? {};
    if (isAuthenticated) this._requireAcceptance(userData ?? null);
  };

  /**
   * `termsAccepted` is null until the user accepts the current terms, then the
   * date they did. The app is usable underneath, but this modal blocks it until
   * they accept (Continue) or leave (Cancel: sign out, then the storefront).
   * Only ever null on a real getUserData() response - dummy data and older
   * backends leave the field out, which means nothing to accept.
   * Not under GrabCAD: the host has its own agreement flow, and a blocking
   * dialog inside the embedded webview would have no storefront to cancel to.
   * The env is resolved off the same getUserData() call, so waiting on
   * envReady here costs nothing.
   */
  private async _requireAcceptance(userData: UserData | null): Promise<void> {
    if (userData?.termsAccepted !== null) return;
    await envReady;
    if (internalData.appEnv === 'grabcad') return;
    // Already up (a keepalive refetch while the user reads the terms): leave
    // their checkbox alone.
    if (panelOverlay(this)?.visibleChildId === this._panelId) return;
    this.reset();
    openPanel(this, this._panelId);
  }

  private async _accept(): Promise<void> {
    if (!this.accepted) return;
    this.setAttribute('busy', '');
    try {
      if (await acceptTerms()) {
        closePanel(this, this._panelId);
        asApp()?.sendLog('terms_accepted');
      }
    } finally {
      this.removeAttribute('busy');
    }
  }

  private async _decline(): Promise<void> {
    this.setAttribute('busy', '');
    try {
      // Same exit as the home button, minus the unsaved-changes prompt: there
      // is nothing to save yet, and the user cannot stay signed in anyway.
      if (await logOut({ reload: false })) {
        closePanel(this, this._panelId);
        asApp()?.redirectToStorefront();
      }
    } finally {
      this.removeAttribute('busy');
    }
  }

  private _syncButtons = (): void => {
    const busy = this.hasAttribute('busy');
    const cont = this.shadowRoot?.querySelector('as-text-button[name="accept-terms"]');
    const cancel = this.shadowRoot?.querySelector('as-text-button[name="decline-terms"]');
    cont?.toggleAttribute('disabled', busy || !this.accepted);
    cancel?.toggleAttribute('disabled', busy);
    if (this.checkbox) this.checkbox.disabled = busy;
  };
}

if (!customElements.get(AsModalAcceptTerms.tag)) {
  customElements.define(AsModalAcceptTerms.tag, AsModalAcceptTerms);
} else {
  console.warn('as-modal-accept-terms already defined');
}
